import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation, OnInit } from '@angular/core';
import { FiOnboardingReportWidgetBaseDirective } from '../fi-onboarding-widget-base.directive';
import {
    IOnboardingReportContainer,
    IOnboardingReportWidgetFilter,
    IOnboardingReportWidgetSettings,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiOnboardingReportDataService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-data.service';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { ISelectedFilterItem } from '@fi-app/shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiOnboardingReportTableFilterHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/shared/fi-onboarding-report-table-filter-helper.service';
import { FiOnboardingReportService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { cloneDeep } from '@fi-shared/utils';
import { distinctUntilChanged, skip } from 'rxjs';
import { isEqual } from '@fi-shared/utils/is-equal';
import { ItemsListFilterDialogSnackbarService } from '@fi-shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Component({
    selector: 'fi-recent-activity-widget',
    templateUrl: './recent-activity-widget.component.html',
    styleUrls: ['./recent-activity-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-recent-activity-widget' },
    standalone: false,
})
export class FiRecentActivityWidgetComponent extends FiOnboardingReportWidgetBaseDirective implements OnInit {
    private fiOnboardingReportDataService = inject(FiOnboardingReportDataService);
    private fiItemsListFilterHelperService = inject(FiItemsListFilterHelperService);
    private fiOnboardingReportTableFilterHelperService = inject(FiOnboardingReportTableFilterHelperService);
    private fiOnboardingReportService = inject(FiOnboardingReportService);
    private itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);

    protected loading = signal(false);
    protected reportContainer = signal<IOnboardingReportContainer>(null);

    protected currentFilter = computed<IOnboardingReportWidgetFilter>(() =>
        this.fiOnboardingReportHelperService.getReportWidgetFilter(this.widget()),
    );
    protected selectedFilterItemsChanged$ = toObservable(this.currentFilter).pipe(
        skip(1),
        distinctUntilChanged(isEqual),
    );
    protected appliedFilterSettingCounter = computed<number>(() => {
        if (!this.currentFilter()) {
            return 0;
        }
        return this.getFilterCounter(this.currentFilter());
    });
    protected isResetButtonVisible = computed<boolean>(() => {
        return this.appliedFilterSettingCounter() > 0;
    });

    protected savedFilters = computed<IOnboardingReportWidgetFilter[]>(() => {
        return this.getNamedFilters(this.widget().Settings.Filters);
    });

    protected isSavedFiltersMenuShown = computed<boolean>(() => {
        return this.savedFilters().length > 0;
    });

    protected getCurrentFilterName = computed<string>(() => {
        const currentFilterName = this.currentFilter()?.Name;
        if (!!currentFilterName?.length) {
            return currentFilterName;
        }
        return this.appTextService.get('common.saved_filters');
    });

    public ngOnInit(): void {
        this.loadReportContainer();
    }

    private loadReportContainer(): void {
        this.fiOnboardingReportDataService.getReportContainer().then((reportContainer: IOnboardingReportContainer) => {
            this.reportContainer.set({ ...reportContainer });
        });
    }

    protected openRecentActivityDialog(): void {
        this.fiOnboardingReportHelperService.openRecentActivityDialog(this.widget());
    }

    protected selectFilter(filterGuid: string): void {
        if (!this.widget().Settings.Filters.some((filter) => filter.Guid === filterGuid)) {
            return;
        }

        this.updateWidgetSettings({
            CurrentFilterGuid: filterGuid,
        });
        this.saveUpdatedRecentActivityWidget();
    }

    protected openRenameSavedFilterDialog(filter: IOnboardingReportWidgetFilter): void {
        this.itemsListFilterDialogSnackbarService
            .getSaveFilterDialog(
                filter.Name,
                this.appTextService.get('filter.rename_filter'),
                'fal fa-pen-to-square BrandedColor',
                this.appTextService.get('filter.update_description'),
            )
            .onYesButtonClick((newFilterName: string) => {
                this.updateSavedFilter({
                    ...filter,
                    Name: newFilterName,
                });
            });
    }

    protected openChangeSavedFilterDialog(filter: IOnboardingReportWidgetFilter): void {
        const selectedFilterItems = this.getSelectedFilterItemsFromFilter(filter);
        this.itemsListFilterDialogSnackbarService
            .getEditFilterDialog(filter.Name, selectedFilterItems)
            .onYesButtonClick((result: ISelectedFilterItem[]) => {
                const updatedFilter = this.getFilterWithoutRemovedSelectedItems(selectedFilterItems, filter, result);
                this.updateSavedFilter(updatedFilter);
            });
    }

    protected openDeleteSavedFilterDialog(filter: IOnboardingReportWidgetFilter): void {
        const isCurrentWidgetFilter = this.widget().Settings.CurrentFilterGuid === filter.Guid;
        this.itemsListFilterDialogSnackbarService.getConfirmDeleteSavedFilterDialog().onYesButtonClick(() => {
            this.updateWidgetSettings({
                CurrentFilterGuid: isCurrentWidgetFilter ? undefined : this.widget().Settings.CurrentFilterGuid,
                Filters: this.getFiltersWithoutFilter(filter),
            });
            this.saveUpdatedRecentActivityWidget();
        });
    }

    protected resetFilter(): void {
        this.updateWidgetSettings({
            CurrentFilterGuid: undefined,
        });
        this.saveUpdatedRecentActivityWidget();
    }

    private updateWidgetSettings(settings: Partial<IOnboardingReportWidgetSettings>): void {
        this.widget.update((widget) => ({
            ...widget,
            Settings: {
                ...widget.Settings,
                ...settings,
            },
        }));
    }

    private updateSavedFilter(filter: IOnboardingReportWidgetFilter): void {
        this.updateWidgetSettings({
            Filters: this.getFiltersWithUpdatedFilter(filter),
        });
        this.saveUpdatedRecentActivityWidget();
    }

    private getFiltersWithUpdatedFilter(filter: IOnboardingReportWidgetFilter): IOnboardingReportWidgetFilter[] {
        return this.widget().Settings.Filters.map((widgetFilter) =>
            widgetFilter.Guid === filter.Guid ? filter : widgetFilter,
        );
    }

    private getFiltersWithoutFilter(filter: IOnboardingReportWidgetFilter): IOnboardingReportWidgetFilter[] {
        return this.widget().Settings.Filters.filter((widgetFilter) => widgetFilter.Guid !== filter.Guid);
    }

    private getNamedFilters(filters: IOnboardingReportWidgetFilter[]): IOnboardingReportWidgetFilter[] {
        return filters.filter((filter) => !!filter.Name?.length);
    }

    private getFilterWithoutRemovedSelectedItems(
        selectedFilterItemsOld: ISelectedFilterItem[],
        filter: IOnboardingReportWidgetFilter,
        newFilterItems: ISelectedFilterItem[],
    ): IOnboardingReportWidgetFilter {
        const updatedFilter = cloneDeep(filter);
        selectedFilterItemsOld
            .filter((savedFilterItem: ISelectedFilterItem) => newFilterItems.indexOf(savedFilterItem) === -1)
            .forEach((removedItem: ISelectedFilterItem) => this.removeFilterItem(updatedFilter, removedItem));
        return this.removeEmptyFilterValues(updatedFilter);
    }

    private removeFilterItem(filter: IOnboardingReportWidgetFilter, removedItem: ISelectedFilterItem): void {
        const filterPropertyName: string = this.fiItemsListFilterHelperService.filterTypeToPropertyNameRecord[removedItem.filterType];

        filter[filterPropertyName].splice(filter[filterPropertyName].indexOf(removedItem.value), 1);
    }

    private removeEmptyFilterValues(filter: IOnboardingReportWidgetFilter): IOnboardingReportWidgetFilter {
        Object.keys(filter).forEach((key) => {
            if (!filter[key as keyof IOnboardingReportWidgetFilter]?.length) {
                delete filter[key as keyof IOnboardingReportWidgetFilter];
            }
        });
        return filter;
    }

    private getSelectedFilterItemsFromFilter(filter: IOnboardingReportWidgetFilter): ISelectedFilterItem[] {
        return this.fiOnboardingReportTableFilterHelperService.getSelectedFilterItemsFromFilter(
            filter,
            this.reportContainer()?.AvailableItems,
        );
    }

    private saveUpdatedRecentActivityWidget(): void {
        this.clearWidgetFromUnsavedFilters();
        this.fiOnboardingReportService.saveReportWidget(this.widget()).then(() => {
            this.fiOnboardingReportDataService.updateRecentActivityWidget(this.widget());
        });
    }

    private clearWidgetFromUnsavedFilters(): void {
        const savedFilters = this.getNamedFilters(this.widget().Settings.Filters);
        const widgetFilterGuid = this.widget().Settings.CurrentFilterGuid;
        const currentFilterGuid = savedFilters.find((filter) => filter.Guid === widgetFilterGuid)
            ? widgetFilterGuid
            : undefined;
        this.updateWidgetSettings({
            CurrentFilterGuid: currentFilterGuid,
            Filters: savedFilters,
        });
    }

    private getFilterCounter(filter: IOnboardingReportWidgetFilter): number {
        return Object.values(filter)
            .filter(Array.isArray)
            .reduce((counter, arr) => counter + arr.length, 0);
    }
}
